import { setGlobalConfig } from 'actions/global.config.action'
import { GlobalConfigType } from 'actions/global.config.type'
import { setLoanGlobalStatus, setMenu } from 'actions/loan.action'
import { setVendor } from 'actions/loanDetail.action'
import { LayoutLoading } from 'components/LayoutLoading'
import { NewVersionPrompt } from 'components/NewVersionPrompt'
import { ThemeApplier } from 'components/ThemeApplier'
import { TimeZoneApplier } from 'components/TimeZoneApplier'
import { setChannelTypeOptions } from 'config/channel.constants'
import { setActiveServicingStatuses, setLoanServicingStatus, setLoanStatusConfig } from 'config/loan.status.constants'
import { setServiceEmail } from 'config/servicingEmails'
import { getTenant, HAS_FRONT_DOOR, refreshTenant, setHostBranding, setTenant } from 'config/tenant.runtime'
import { setAccountTypeBaseTypes, setAccountTypes, setAccountTypeTitles } from 'config/user.type.constants'
import { BaseLayout } from 'layouts'
import { setLoanFieldDefaultSection } from 'pages/Admin/AdminTools/Configuration/LoanFieldsProperties/utils'
import { Suspense, useEffect, useState } from 'react'
import TagManager from 'react-gtm-module'
import { QueryClient, QueryClientProvider } from 'react-query'
import { ReactQueryDevtools } from 'react-query/devtools'
import { useDispatch } from 'react-redux'
import { Switch } from 'react-router-dom'
import { applyChannelOptions } from 'reducers/loanDetail.reducer'
import { BaseRoute, TemporaryRoute } from 'routes'
import { getAdminConfig } from 'services/apis'
import { frontDoorBootstrap } from 'services/apis/frontDoor'
import { hexToRgbSpace, phoneConvertor, setLoanNumberPrefix } from 'utils'
import { getBrokerHostSlug } from 'utils/hostWhiteLabel'
import { setCompanyTimeZone } from 'utils/timezone'
import { trackApplicationStart } from 'utils/utm'

const queryClient = new QueryClient()

function App() {
  const dispatch = useDispatch()

  const [isLoading, setLoading] = useState(true)

  useEffect(() => {
    trackApplicationStart()

    const host = typeof window !== 'undefined' ? window.location.hostname : ''
    const brokerSlug = getBrokerHostSlug(host)
    // Host branding rides on public appInit (same call) so a vanity-host lookup can never
    // block boot or trip Api 401 error handling via a separate request.
    const loadAppConfig = () =>
      getAdminConfig('appInit', brokerSlug ? { host } : {})
        .then((data) => {
          const {
            loanStatus,
            menu,
            vendorDetail,
            pagePermissions,
            footerConfig,
            conditionTypeOptions,
            documentCategoryOptions,
            globalSettingV2,
            loanFieldProperties,
            loanSubmissionHierarchy,
            guaranter,
            orderValuationOptions,
            accountTypes,
            accountTypeTitles,
            accountTypeBaseTypes,
            loanServicingStatus,
            activeServicingStatuses,
            config,
            parties,
            emailOptions,
            serviceEmail,
            rulesConfig,
            ineligibleParties,
            secondaryMarketing,
            paymentCodes,
            paymentMethods,
            accountPayees,
            accountSections,
            accountingAccountTypes,
            emailModalDefaults,
            defaultSenderEmailByAccountType,
            loanSubStatus,
            shippingInfoHeaders,
            uwConditionCategories,
            hostWhiteLabel,
          } = data
          let loanStatusObj: any = {}
          Object.keys(loanStatus).map((key) => {
            if (loanStatus[key].use) loanStatusObj[key] = loanStatus[key].new
          })
          // accountTypes is already in-use only (key → display title). Do not merge the built-in
          // originals — that left unchecked roles like "Loan Processor - Retail" in every dropdown.
          setAccountTypes(accountTypes || {})
          setAccountTypeTitles(accountTypeTitles || accountTypes || {})
          setAccountTypeBaseTypes(accountTypeBaseTypes || {})
          setChannelTypeOptions(applyChannelOptions(globalSettingV2?.channelOptions))
          // Company fallback zone for users who have not picked one of their own.
          setCompanyTimeZone(globalSettingV2?.defaultTimezone)
          setLoanServicingStatus(loanServicingStatus)
          setActiveServicingStatuses(activeServicingStatuses)
          setLoanStatusConfig(loanStatus)

          setLoanNumberPrefix(config?.loanNumberPrefix || '')
          // The tenant's own servicing mailbox, so a document email copies the address they read.
          setServiceEmail(serviceEmail)

          dispatch(setLoanGlobalStatus(loanStatusObj))
          dispatch(setMenu(menu))
          dispatch(setVendor(vendorDetail))
          dispatch(setGlobalConfig(GlobalConfigType.LoanStatus, loanStatus))
          dispatch(setGlobalConfig(GlobalConfigType.ConditionTypes, conditionTypeOptions))
          dispatch(setGlobalConfig(GlobalConfigType.DocumentCategory, documentCategoryOptions))
          dispatch(setGlobalConfig(GlobalConfigType.PagePermissions, pagePermissions))
          if (footerConfig?.phone) footerConfig.phone.value = phoneConvertor(footerConfig.phone.value)
          if (footerConfig?.tollFree) footerConfig.tollFree.value = phoneConvertor(footerConfig.tollFree.value)
          dispatch(setGlobalConfig(GlobalConfigType.Footer, footerConfig))
          dispatch(setGlobalConfig(GlobalConfigType.GlobalSettingV2, globalSettingV2))
          dispatch(
            setGlobalConfig(
              GlobalConfigType.LoanFields,
              setLoanFieldDefaultSection(loanFieldProperties, { combineLinkedFields: true }),
            ),
          )
          dispatch(setGlobalConfig(GlobalConfigType.LoanSubmissionHierarchy, loanSubmissionHierarchy))
          dispatch(setGlobalConfig(GlobalConfigType.GUARANTER_CONFIG, guaranter))
          dispatch(setGlobalConfig(GlobalConfigType.ORDER_VALUATION, orderValuationOptions))
          dispatch(setGlobalConfig(GlobalConfigType.ACCOUNT_TYPES, accountTypes))
          dispatch(setGlobalConfig(GlobalConfigType.PARTIES, parties))
          dispatch(setGlobalConfig(GlobalConfigType.EMAIL_OPTIONS, emailOptions))
          dispatch(setGlobalConfig(GlobalConfigType.GTM, config))
          dispatch(setGlobalConfig(GlobalConfigType.RulesConfig, rulesConfig))
          dispatch(setGlobalConfig(GlobalConfigType.IneligibleParties, ineligibleParties))
          dispatch(setGlobalConfig(GlobalConfigType.SecondaryMarketing, secondaryMarketing))
          dispatch(setGlobalConfig(GlobalConfigType.PAYMENT_CODE, paymentCodes))
          dispatch(setGlobalConfig(GlobalConfigType.PAYMENT_METHOD, paymentMethods || []))
          dispatch(setGlobalConfig(GlobalConfigType.ACCOUNT_PAYEES, accountPayees))
          dispatch(setGlobalConfig(GlobalConfigType.ACCOUNT_SECTIONS, accountSections))
          dispatch(setGlobalConfig(GlobalConfigType.ACCOUNTING_ACCOUNT_TYPES, accountingAccountTypes))
          dispatch(setGlobalConfig(GlobalConfigType.EmailModalDefaults, emailModalDefaults || {}))
          dispatch(
            setGlobalConfig(GlobalConfigType.DefaultSenderEmailByAccountType, defaultSenderEmailByAccountType || []),
          )
          dispatch(setGlobalConfig(GlobalConfigType.LoanSubStatus, loanSubStatus || {}))
          dispatch(setGlobalConfig(GlobalConfigType.ShippingInfoHeaders, shippingInfoHeaders || {}))
          dispatch(setGlobalConfig(GlobalConfigType.UWConditionCategories, uwConditionCategories || []))
          const brand =
            hostWhiteLabel && typeof hostWhiteLabel === 'object' && !Array.isArray(hostWhiteLabel)
              ? hostWhiteLabel
              : null
          dispatch(setGlobalConfig(GlobalConfigType.HostWhiteLabel, brand))

          if (config?.gtmId) {
            TagManager.initialize({
              gtmId: config?.gtmId, // GTM Code
            })
          }

          if (config?.hubspotEmbedId) {
            var hsScript = document.createElement('script')
            hsScript.type = 'text/javascript'
            hsScript.id = 'hs-script-loader'
            hsScript.async = true
            hsScript.defer = true
            hsScript.src = `//js.hs-scripts.com/${config?.hubspotEmbedId}.js`
            document.head.appendChild(hsScript)
          }

          if (config?.ui) {
            // Set the brand hues on --brand-* (not --color-* directly) so per-user
            // site styles like Classic can still override the accent via [data-theme].
            // :root maps --color-primary/-shade-blue to these with a modern fallback.
            document.documentElement.style.setProperty('--brand-primary', hexToRgbSpace(config.ui.primary))
            document.documentElement.style.setProperty('--brand-secondary', hexToRgbSpace(config.ui.secondary))
          }
        })
        .finally(() => setLoading(false))

    // At the shared front door nobody has signed in yet, so there is no tenant and therefore no
    // backend to ask for appInit. Brand the sign-in page from the front door instead and stop:
    // calling appInit here would fire a request at whatever API this bundle happened to be built
    // against, which for a shared build is nothing at all.
    // Already signed in: re-read the tenant so a change to its logo or name is picked up
    // without signing in again. Fire and forget - branding must never gate boot.
    if (HAS_FRONT_DOOR && getTenant()) refreshTenant()

    if (HAS_FRONT_DOOR && !getTenant()) {
      frontDoorBootstrap().then((data) => {
        // Adopt the tenant, not only its branding. A browser whose session predates the shared
        // build has a token but no stored tenant, so without this every API call falls back to
        // a relative URL, resolves to the app itself, and comes back as HTML.
        if (data?.tenant?.apiUrl) setTenant(data.tenant, true)

        const branding = data?.branding
        if (branding) {
          // Applied before sign-in so a customer arriving at their own hostname sees their own
          // login page rather than a neutral one. The host is what identifies them; no
          // credential has been offered yet.
          setHostBranding(branding)
          if (branding.title) document.title = branding.title
          if (branding.primaryColor) {
            document.documentElement.style.setProperty('--brand-primary', hexToRgbSpace(branding.primaryColor))
          }
          if (branding.secondaryColor) {
            document.documentElement.style.setProperty('--brand-secondary', hexToRgbSpace(branding.secondaryColor))
          }
        }
        // Adopting a tenant here means there IS a backend to configure against now, so carry
        // on and load it. Returning at this point would leave a signed-in user with an app that
        // has no statuses, menus or permissions until they happened to reload.
        if (getTenant()) {
          loadAppConfig()
          return
        }

        // Shared front door, no tenant: nothing to configure against and nobody signed in.
        setLoading(false)
      })
      return
    }

    loadAppConfig()
  }, [])

  if (isLoading) return <LayoutLoading show />

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeApplier />
      <NewVersionPrompt />
      <TimeZoneApplier>
        <Suspense fallback={<LayoutLoading show={true} />}>
          <Switch>
            {TemporaryRoute}
            <BaseLayout>
              <BaseRoute />
            </BaseLayout>
          </Switch>
        </Suspense>
      </TimeZoneApplier>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}

export default App
